// Story 7.22 (ADO-1703) — the session depot-name read the SPA hydrates into
// `useSessionStore.depotNames`.
//
// `GET /api/v1/session/depots` (served by the apps/api `SessionDepotsController`,
// `JwtAuthGuard` only) returns the caller's own depots as `[{ id, name }]` with
// the REAL depot name from membership's depot facts (Enabler E7-B, ADO-2273, D-2;
// the code where membership holds no name), as SL1.0 names it: by its ordering
// environment ("Ambient", not "NDC KILCARBERY"), a Value Centre by its depot name
// (`depotDisplayName`, @shoplink/tenant-context), resolving #1219 D-5 / OQ-2. It replaces the hashed "VC ..."
// placeholder `depotDisplay.ts` invented from the depot id.
//
// DEGRADES GRACEFULLY — any non-200, network failure, or parse error resolves to
// an EMPTY map (the SPA then shows each depot's id as the neutral fallback, never
// a fabricated name). It NEVER throws and NEVER blocks bootstrap. Read over the
// Bearer-attaching `apiFetch` transport (the API stays the source of truth).

import { API_PREFIX, apiFetch } from './shoplink-api';

/** The session depots read (Story 7.22 / ADO-1703). */
export const SESSION_DEPOTS_PATH = `${API_PREFIX}/session/depots`;

/**
 * Read the caller's depots and return a `{ id → name }` map. Returns an empty
 * map on any failure (network, non-200, parse) — callers treat that as "names
 * not yet known" and fall back to the depot id.
 */
export async function fetchSessionDepotNames(): Promise<Readonly<Record<string, string>>> {
  try {
    const res = await apiFetch(SESSION_DEPOTS_PATH);
    if (!res.ok) return {};
    const body = (await res.json()) as unknown;
    if (!Array.isArray(body)) return {};
    const names: Record<string, string> = {};
    for (const entry of body) {
      if (entry === null || typeof entry !== 'object') continue;
      const { id, name } = entry as { id?: unknown; name?: unknown };
      if (typeof id === 'string' && id !== '' && typeof name === 'string' && name !== '') {
        names[id] = name;
      }
    }
    return names;
  } catch {
    return {};
  }
}
